/**
 * Officer-type grouping/coloring shared by the auto-assign modal, the stats
 * filter, and the "free up counter time" picker — one place for "what color
 * is this officer type" and "what order do groups come in", so a reorder or
 * a palette tweak never has to happen in more than one file, and all three
 * read alike.
 *
 * The order groups come in is whatever naturalSortOfficers (the officer
 * graph's own row order) actually produces — never a separately hand-picked
 * MAIN/SOS/OT list, which is exactly what let this drift out of sync with
 * the graph before (officer keys sort OT < S < SN, so the graph reads
 * OT, then SOS, then Main).
 */
import { naturalSortOfficers } from '@/config/modeConfig';

export type OfficerTypeGroup = 'MAIN' | 'SOS' | 'OT';

export const OFFICER_TYPE_PILL: Record<OfficerTypeGroup, { bg: string; fg: string }> = {
  SOS: { bg: '#ffc6d9', fg: '#9d174d' },
  OT: { bg: '#FDFD96', fg: '#713f12' },
  MAIN: { bg: '#a2d2ff', fg: '#1e40af' },
};

/** Derives an officer's type group from its metadata (when known) and its key
 *  prefix otherwise — the same fallback every call site used to spell out by hand. */
export function officerTypeOf(id: string, officerType?: string): OfficerTypeGroup {
  const t = (officerType ?? '').toUpperCase();
  if (t === 'MAIN') return 'MAIN';
  if (t === 'OT' || id.startsWith('OT')) return 'OT';
  return 'SOS';
}

/**
 * Groups items by officer type, in the SAME order officer keys naturally sort
 * in (naturalSortOfficers) — the officer graph's own order — rather than a
 * second, independently-chosen ordering that can drift from it.
 */
export function groupOfficersByType<T>(
  items: T[],
  keyOf: (item: T) => string,
  typeOf: (item: T) => OfficerTypeGroup,
): Array<{ type: OfficerTypeGroup; items: T[] }> {
  const byKey = new Map(items.map(item => [keyOf(item), item]));
  const ordered = naturalSortOfficers(items.map(keyOf));
  const groups: Array<{ type: OfficerTypeGroup; items: T[] }> = [];
  for (const key of ordered) {
    const item = byKey.get(key);
    if (!item) continue;
    const type = typeOf(item);
    const last = groups[groups.length - 1];
    if (last && last.type === type) last.items.push(item);
    else groups.push({ type, items: [item] });
  }
  return groups;
}
